import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type {
  Print,
  PrintInsert,
  PrintUpdate,
  PrintFilamentUsageInsert,
  PrintBuild,
} from '../lib/database.types'

const PRINTS_KEY = ['prints']
const PRINT_BUILDS_KEY = ['print_builds']
const SPOOLS_KEY = ['filament_spools']
const PRINTERS_KEY = ['printers']
const BUILDS_KEY = ['builds']
const PRINT_USAGE_KEY = ['print_filament_usage']

export interface PrintFilamentUsageRow {
  print_id: string
  spool_id: string
  grams_used: number
}

export function usePrintFilamentUsage() {
  return useQuery({
    queryKey: PRINT_USAGE_KEY,
    queryFn: async (): Promise<PrintFilamentUsageRow[]> => {
      const { data, error } = await supabase
        .from('print_filament_usage')
        .select('print_id, spool_id, grams_used')
      if (error) throw error
      return data ?? []
    },
  })
}

export const PRINT_PURPOSES = [
  'business-sale',
  'business-test',
  'personal',
  'gift',
] as const
export const PRINT_OUTCOMES = ['success', 'partial-fail', 'full-fail'] as const

export function usePrints() {
  return useQuery({
    queryKey: PRINTS_KEY,
    queryFn: async (): Promise<Print[]> => {
      const { data, error } = await supabase
        .from('prints')
        .select('*')
        .order('print_date', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

// All print↔build links. Used by inventory + print-edit screens to know which
// builds a print contributes to (a single plate can produce multiple products).
export function usePrintBuilds() {
  return useQuery({
    queryKey: PRINT_BUILDS_KEY,
    queryFn: async (): Promise<PrintBuild[]> => {
      const { data, error } = await supabase
        .from('print_builds')
        .select('*')
      if (error) throw error
      return data ?? []
    },
  })
}

async function reconcilePrinterHours(printerId: string): Promise<void> {
  // Both read + write must throw on failure. Silent errors here used to
  // corrupt total_print_hours: a failed SELECT returned an empty array,
  // making `total` collapse to 0, which then overwrote the real value.
  const { data: prints, error: readErr } = await supabase
    .from('prints')
    .select('print_time_hours')
    .eq('printer_id', printerId)
  if (readErr) throw readErr
  const total = (prints ?? []).reduce((sum, p) => sum + Number(p.print_time_hours), 0)
  const { error: writeErr } = await supabase
    .from('printers')
    .update({ total_print_hours: total })
    .eq('id', printerId)
  if (writeErr) throw writeErr
}

async function reconcileSpoolWeight(spoolId: string): Promise<void> {
  const { data: spool, error: spoolErr } = await supabase
    .from('filament_spools')
    .select('original_weight_grams, current_weight_grams, manual_weight_adjustment_grams')
    .eq('id', spoolId)
    .single()
  if (spoolErr) throw spoolErr
  if (!spool) return
  const { data: allUsages, error: usageErr } = await supabase
    .from('print_filament_usage')
    .select('grams_used')
    .eq('spool_id', spoolId)
  if (usageErr) throw usageErr
  const totalUsed = (allUsages ?? []).reduce((sum, u) => sum + u.grams_used, 0)
  // Manual reweighs (kitchen-scale corrections) are captured in
  // manual_weight_adjustment_grams by useUpdateFilamentSpool, so adding
  // it back here keeps those corrections sticky across future prints.
  const expectedWeight =
    spool.original_weight_grams + spool.manual_weight_adjustment_grams - totalUsed
  if (spool.current_weight_grams !== expectedWeight) {
    const { error: updErr } = await supabase
      .from('filament_spools')
      .update({ current_weight_grams: expectedWeight })
      .eq('id', spoolId)
    if (updErr) throw updErr
  }
}

// Replace the junction rows for a print with exactly `buildIds`.
// Also keeps prints.build_id in sync with the first build (denormalized "primary").
async function syncPrintBuilds(
  printId: string,
  userId: string,
  buildIds: string[],
): Promise<void> {
  // Wipe the junction table first; a silent failure here would leave the
  // old links in place and double up the print across builds.
  const { error: delErr } = await supabase
    .from('print_builds')
    .delete()
    .eq('print_id', printId)
  if (delErr) throw delErr
  if (buildIds.length > 0) {
    const rows = buildIds.map((build_id) => ({
      user_id: userId,
      print_id: printId,
      build_id,
    }))
    const { error } = await supabase.from('print_builds').insert(rows)
    if (error) throw error
  }
  const { error: primaryErr } = await supabase
    .from('prints')
    .update({ build_id: buildIds[0] ?? null })
    .eq('id', printId)
  if (primaryErr) throw primaryErr
}

export interface CreatePrintInput {
  print: Omit<PrintInsert, 'user_id'>
  usages: Array<Omit<PrintFilamentUsageInsert, 'user_id' | 'print_id'>>
  // Optional explicit list of builds this print contributes to. When provided,
  // it OVERRIDES print.build_id. First entry becomes the "primary" build.
  // When omitted, falls back to [print.build_id] if non-null, else [].
  buildIds?: string[]
}

export function useCreatePrint() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ print, usages, buildIds }: CreatePrintInput): Promise<Print> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')

      const resolvedBuildIds =
        buildIds ?? (print.build_id ? [print.build_id] : [])
      const primaryBuildId = resolvedBuildIds[0] ?? null

      const { data: createdPrint, error: printErr } = await supabase
        .from('prints')
        .insert({ ...print, build_id: primaryBuildId, user_id: user.id })
        .select('*')
        .single()
      if (printErr) throw printErr

      if (resolvedBuildIds.length > 0) {
        const rows = resolvedBuildIds.map((build_id) => ({
          user_id: user.id,
          print_id: createdPrint.id,
          build_id,
        }))
        const { error: linkErr } = await supabase.from('print_builds').insert(rows)
        if (linkErr) {
          // Best-effort rollback. If the cleanup fails (RLS denial etc.)
          // surface that too so the user knows an orphan print row exists
          // and can investigate, rather than seeing a generic insert error.
          const { error: cleanupErr } = await supabase.from('prints').delete().eq('id', createdPrint.id)
          if (cleanupErr) {
            throw new Error(
              `Failed to link build (${linkErr.message}); rollback also failed (${cleanupErr.message}). Orphan print row id=${createdPrint.id} may need manual cleanup.`,
            )
          }
          throw linkErr
        }
      }

      if (usages.length > 0) {
        const usageRows = usages.map((u) => ({
          ...u,
          user_id: user.id,
          print_id: createdPrint.id,
        }))
        const { error: usageErr } = await supabase
          .from('print_filament_usage')
          .insert(usageRows)
        if (usageErr) {
          const { error: cleanupErr } = await supabase.from('prints').delete().eq('id', createdPrint.id)
          if (cleanupErr) {
            throw new Error(
              `Failed to log filament usage (${usageErr.message}); rollback also failed (${cleanupErr.message}). Orphan print row id=${createdPrint.id} may need manual cleanup.`,
            )
          }
          throw usageErr
        }

        const uniqueSpoolIds = Array.from(new Set(usages.map((u) => u.spool_id)))
        for (const spoolId of uniqueSpoolIds) {
          await reconcileSpoolWeight(spoolId)
        }
      }

      await reconcilePrinterHours(createdPrint.printer_id)

      return createdPrint
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRINTS_KEY })
      queryClient.invalidateQueries({ queryKey: PRINT_BUILDS_KEY })
      queryClient.invalidateQueries({ queryKey: SPOOLS_KEY })
      queryClient.invalidateQueries({ queryKey: PRINTERS_KEY })
      queryClient.invalidateQueries({ queryKey: BUILDS_KEY })
    },
  })
}

export interface UpdatePrintInput {
  id: string
  updates: PrintUpdate
  // When provided, replaces all filament usage rows for this print.
  usages?: Array<{ spool_id: string; grams_used: number; cost_recognized: number }>
  // When provided, replaces all build attachments. First entry becomes
  // prints.build_id (the "primary" build). Undefined = leave untouched.
  buildIds?: string[]
}

export function useUpdatePrint() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, updates, usages, buildIds }: UpdatePrintInput): Promise<Print> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')

      const { data: priorPrint } = await supabase
        .from('prints')
        .select('printer_id')
        .eq('id', id)
        .single()
      const priorPrinterId = priorPrint?.printer_id

      // If buildIds provided, sync junction first (this also rewrites prints.build_id).
      // Then apply the rest of the updates (excluding build_id to avoid clobbering).
      if (buildIds !== undefined) {
        await syncPrintBuilds(id, user.id, buildIds)
        // Strip build_id from updates payload — the sync just set it correctly.
        delete (updates as { build_id?: unknown }).build_id
      }

      const { data, error } = await supabase
        .from('prints')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error

      if (usages !== undefined) {
        const { data: oldUsages, error: oldUsagesErr } = await supabase
          .from('print_filament_usage')
          .select('spool_id')
          .eq('print_id', id)
        if (oldUsagesErr) throw oldUsagesErr
        const oldSpoolIds = new Set((oldUsages ?? []).map((u) => u.spool_id))

        const { error: usageDelErr } = await supabase
          .from('print_filament_usage')
          .delete()
          .eq('print_id', id)
        if (usageDelErr) throw usageDelErr

        if (usages.length > 0) {
          const rows = usages.map((u) => ({
            ...u,
            user_id: user.id,
            print_id: id,
          }))
          const { error: usageErr } = await supabase.from('print_filament_usage').insert(rows)
          if (usageErr) throw usageErr
        }

        const newSpoolIds = new Set(usages.map((u) => u.spool_id))
        const allAffected = new Set([...oldSpoolIds, ...newSpoolIds])
        for (const spoolId of allAffected) {
          await reconcileSpoolWeight(spoolId)
        }
      }

      await reconcilePrinterHours(data.printer_id)
      if (priorPrinterId && priorPrinterId !== data.printer_id) {
        await reconcilePrinterHours(priorPrinterId)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRINTS_KEY })
      queryClient.invalidateQueries({ queryKey: PRINT_BUILDS_KEY })
      queryClient.invalidateQueries({ queryKey: SPOOLS_KEY })
      queryClient.invalidateQueries({ queryKey: PRINTERS_KEY })
      queryClient.invalidateQueries({ queryKey: BUILDS_KEY })
    },
  })
}

export function useDeletePrint() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { data: printBefore } = await supabase
        .from('prints')
        .select('printer_id')
        .eq('id', id)
        .single()
      const printerId = printBefore?.printer_id
      const { data: usagesBefore } = await supabase
        .from('print_filament_usage')
        .select('spool_id')
        .eq('print_id', id)
      const affectedSpoolIds = Array.from(
        new Set((usagesBefore ?? []).map((u) => u.spool_id))
      )

      const { error } = await supabase.from('prints').delete().eq('id', id)
      if (error) throw error

      for (const spoolId of affectedSpoolIds) {
        await reconcileSpoolWeight(spoolId)
      }

      if (printerId) {
        await reconcilePrinterHours(printerId)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRINTS_KEY })
      queryClient.invalidateQueries({ queryKey: PRINT_BUILDS_KEY })
      queryClient.invalidateQueries({ queryKey: SPOOLS_KEY })
      queryClient.invalidateQueries({ queryKey: PRINTERS_KEY })
      queryClient.invalidateQueries({ queryKey: BUILDS_KEY })
    },
  })
}
